import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { APPOWNER } from "../utils/utils.constants";
import { routes } from "../utils/utils.routes";
import { toast } from "react-toastify";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FiLock } from "react-icons/fi";
import { IoMailOutline } from "react-icons/io5";
import logo from "../assets/logo.png";
import { useLanguage } from "../contexts/LanguageContext";

const MailI = IoMailOutline as any;
const LockI = FiLock as any;

const LoginSchema = Yup.object().shape({
  email: Yup.string().email("Adresse email invalide").required("L'adresse email est requise"),
  password: Yup.string().min(6, "Le mot de passe doit contenir au moins 6 caractères").required("Le mot de passe est requis"),
});

export const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [apiError, setApiError] = useState("");

  return (
    <div data-testid="login-page" className="login-page">
      <aside className="login-page__hero" aria-hidden={false}>
        <div className="login-page__hero-inner">
          <div className="login-page__hero-brand">
            <img
              src={logo}
              alt="ULPGL"
              width={50}
              height={50}
              style={{
                background: "rgba(255,255,255, .2)",
                padding: 5,
                paddingTop: 8,
                borderBottomRightRadius: 23,
                borderBottomLeftRadius: 23
              }}
            />
            <div className="title d-flex" style={{ height: 55, paddingTop: 0, paddingBottom: 0, flexDirection: "column" }}>
              <span className="login-page__hero-desc- p-0 m-0" style={{ fontSize: 16 }}>ULPGL • GOMA</span>
              <p className="login-page__hero-desc p-0 m-0">Université Libre des Pays des Grands Lacs</p>
            </div>
          </div>
          <div className="login-page__hero-content">
            <h1 className="login-page__hero-title">
              {t.login.heroTitle}
              <br />
              <span className="login-page__hero-accent">{t.login.heroAccent}</span>
            </h1>
            <p className="login-page__hero-desc">{t.login.heroDesc}</p>
          </div>
          <p className="login-page__hero-footer">© {new Date().getFullYear()} {APPOWNER}</p>
        </div>
      </aside>

      <div className="login-page__form-side">
        <div className="login-page__form-wrap">
          <p className="login-page__eyebrow">{t.login.welcome}</p>
          <h2 className="login-page__title">{t.login.title}</h2>
          <p className="login-page__subtitle">{t.login.subtitle}</p>

          <Formik
            initialValues={{ email: "", password: "" }}
            validationSchema={LoginSchema}
            onSubmit={async (values, { setSubmitting }) => {
              setApiError("");
              try {
                await login(values.email, values.password);
                navigate(routes.ADMIN);
              } catch (err: any) {
                const msg = err?.response?.data?.reason || err?.messageText || "Identifiants invalides";
                setApiError(msg);
                toast.error(msg);
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ errors, touched, isSubmitting }) => (
              <Form>
                <div className="login-page__field">
                  <label htmlFor="login-email">Email</label>
                  <div className="login-page__input-wrap">
                    <MailI size={18} />
                    <Field
                      id="login-email"
                      data-testid="login-email-input"
                      name="email"
                      type="email"
                      placeholder="mon-adresse-mail@ulpgl.net"
                      className={touched.email && errors.email ? "invalid" : ""}
                    />
                  </div>
                  <ErrorMessage name="email" component="div" className="text-dang" />
                </div>

                <div className="login-page__field">
                  <label htmlFor="login-password">{t.login.password}</label>
                  <div className="login-page__input-wrap">
                    <LockI size={18} />
                    <Field
                      id="login-password"
                      data-testid="login-password-input"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      className={touched.password && errors.password ? "invalid" : ""}
                    />
                  </div>
                  <ErrorMessage name="password" component="div" className="text-dang" />
                </div>
                {/* <div className="row">
                  <div className="col-6"></div>
                  <div className="col-6">
                    <Link
                      to={routes.FORGOT_PASSWORD}
                      className="login-page__field text-right"
                      style={{
                        display: "flex",
                        flex: 1,
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: "6px",
                        color: Colors.primaryColor
                      }}
                    >
                      <ArrowRight size={12} />
                      <label style={{ margin: 0, cursor: "pointer" }}>
                        {t.login.forgotPassword} ?
                      </label>
                    </Link>
                  </div>
                </div> */}

                {apiError && (
                  <div data-testid="login-error" className="login-page__error">{apiError}</div>
                )}

                <button
                  data-testid="login-submit-button"
                  type="submit"
                  disabled={isSubmitting}
                  className="login-page__submit"
                >
                  {isSubmitting ? t.login.submitting : t.login.submit}
                </button>

                <p className="login-page__help">{t.login.forgotHelp}</p>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
